import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataRoomService } from '@/services/dataRoomService';
import { Project } from '@/types/project';

// Simple debounce utility
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  offset: number;
}

interface UseFundingRoundsPaginationReturn {
  projects: Project[];
  loading: boolean;
  error: string | null;
  pagination: PaginationState;
  search: string;
  sortBy: 'DATE' | 'PROJECT_NAME' | 'ORDERS';
  sortDirection: 'ASC' | 'DESC';
  setSearch: (search: string) => void;
  setSortBy: (sortBy: 'DATE' | 'PROJECT_NAME' | 'ORDERS') => void;
  setSortDirection: (direction: 'ASC' | 'DESC') => void;
  goToPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  refresh: () => void;
}

export const useFundingRoundsPagination = (): UseFundingRoundsPaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL state
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const limit = searchParams.get('limit');
    return limit ? Math.min(parseInt(limit, 10), 10) : 10; // Max 10 per API limit
  });
  
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState<'DATE' | 'PROJECT_NAME' | 'ORDERS'>(() => {
    const sort = searchParams.get('sort_by') as 'DATE' | 'PROJECT_NAME' | 'ORDERS';
    return sort || 'ORDERS';
  });
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>(() => {
    const direction = searchParams.get('sort_direction') as 'ASC' | 'DESC';
    return direction || 'DESC';
  });

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    offset: 0,
  });

  // Update URL parameters
  const updateURLParams = useCallback((updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * itemsPerPage;
      
      const response = await dataRoomService.getFundingRounds({
        limit: itemsPerPage,
        offset,
        search: search || undefined,
        sort_by: sortBy,
        sort_direction: sortDirection,
        include_project_stats: true,
        include_rounds: true,
      });

      setProjects(response.data);
      setPagination({
        currentPage,
        totalPages: response.total_pages || Math.ceil(response.total / itemsPerPage),
        totalItems: response.total,
        itemsPerPage,
        offset,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch funding rounds');
      console.error('Funding rounds fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, search, sortBy, sortDirection]);

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      // Cleanup debounced functions
    };
  }, []);

  // Update URL when state changes
  useEffect(() => {
    updateURLParams({
      page: currentPage,
      limit: itemsPerPage,
      search,
      sort_by: sortBy,
      sort_direction: sortDirection,
    });
  }, [currentPage, itemsPerPage, search, sortBy, sortDirection, updateURLParams]);

  // Debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((newSearch: string) => {
      setSearch(newSearch);
      setCurrentPage(1); // Reset to first page when searching
    }, 300),
    []
  );

  // Handlers
  const handleSearch = useCallback((newSearch: string) => {
    debouncedSearch(newSearch);
  }, [debouncedSearch]);

  const handleSortBy = useCallback((newSortBy: 'DATE' | 'PROJECT_NAME' | 'ORDERS') => {
    setSortBy(newSortBy);
    setCurrentPage(1); // Reset to first page when sorting
  }, []);

  const handleSortDirection = useCallback((newDirection: 'ASC' | 'DESC') => {
    setSortDirection(newDirection);
    setCurrentPage(1); // Reset to first page when sorting
  }, []);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  }, [pagination.totalPages]);

  const handleItemsPerPage = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    projects,
    loading,
    error,
    pagination,
    search,
    sortBy,
    sortDirection,
    setSearch: handleSearch,
    setSortBy: handleSortBy,
    setSortDirection: handleSortDirection,
    goToPage,
    setItemsPerPage: handleItemsPerPage,
    refresh,
  };
}; 